import React, { useState, useEffect } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Alert,
	Button,
	CircularProgress,
} from "@mui/material";
import {
	CheckCircle as CheckCircleIcon,
	RadioButtonUnchecked as RadioButtonUncheckedIcon,
	ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const ProfileCompletionSection = ({ user, supabase }) => {
	const [profileData, setProfileData] = useState(null);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchProfile = async () => {
			try {
				// Fetch profile data
				const { data: profile, error: profileError } = await supabase
					.from("profiles")
					.select("*")
					.eq("id", user.id)
					.single();

				if (profileError) {
					// Handle profile not found gracefully
					if (profileError.code === "PGRST116") {
						console.warn("Profile not found for user in ProfileCompletionSection:", user.id);
						setProfileData(null);
						setLoading(false);
						return;
					}
					throw profileError;
				}

				// Fetch teacher's grades
				const { data: teacherGrades, error: gradesError } = await supabase
					.from("teacher_grades")
					.select("grade_id")
					.eq("profile_id", user.id);

				if (gradesError) {
					console.warn("Error fetching teacher grades:", gradesError);
					// Continue with empty grades array
				}

				setProfileData({
					...profile,
					grades: teacherGrades || [],
				});
			} catch (error) {
				console.error("Error fetching profile:", error);
				setProfileData(null);
			} finally {
				setLoading(false);
			}
		};

		fetchProfile();
	}, [user.id, supabase]);

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (!profileData || profileData.role !== "ROLE_SCHOOL_TEACHER") {
		return null;
	}

	const tasks = [
		{
			id: "name",
			label: "Add your full name",
			completed: Boolean(profileData.full_name),
		},
		{
			id: "phone",
			label: "Add your WhatsApp number",
			completed: Boolean(profileData.phone_number),
		},
		{
			id: "school",
			label: "Select your school",
			completed: Boolean(profileData.school_id),
		},
		{
			id: "grades",
			label: "Select your teaching grades",
			completed: profileData.grades.length > 0,
		},
	];

	const incompleteTasks = tasks.filter((task) => !task.completed);
	if (incompleteTasks.length === 0) return null;

	return (
		<Card sx={{ mb: 3, border: "1px solid #e5e7eb" }}>
			<CardContent>
				<Alert severity="warning" sx={{ mb: 3 }}>
					<Typography variant="h6" component="div" sx={{ mb: 1 }}>
						Complete Your Profile
					</Typography>
					<Typography variant="body2">
						Please complete your profile to get started. You have{" "}
						{incompleteTasks.length}{" "}
						{incompleteTasks.length === 1 ? "task" : "tasks"} remaining.
					</Typography>
				</Alert>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{tasks.map((task) => (
						<Box
							key={task.id}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								{task.completed ? (
									<CheckCircleIcon color="success" />
								) : (
									<RadioButtonUncheckedIcon sx={{ color: "#f59e0b" }} />
								)}
								<Typography
									sx={{
										color: task.completed ? "text.secondary" : "text.primary",
										fontWeight: task.completed ? "normal" : 500,
									}}>
									{task.label}
								</Typography>
							</Box>
							{!task.completed && (
								<Button
									size="small"
									endIcon={<ArrowForwardIcon />}
									onClick={() => navigate("/account")}
									sx={{ color: "#f59e0b" }}>
									Complete
								</Button>
							)}
						</Box>
					))}
				</Box>

				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
					<Button
						variant="contained"
						endIcon={<ArrowForwardIcon />}
						onClick={() => navigate("/account")}
						sx={{
							backgroundColor: "#f59e0b",
							"&:hover": {
								backgroundColor: "#d97706",
							},
						}}>
						Complete Profile
					</Button>
				</Box>
			</CardContent>
		</Card>
	);
};

export default ProfileCompletionSection;
